import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ColDef, GridApi, GridReadyEvent, SideBarDef } from '@ag-grid-community/core';
import { GRID_CONFIG } from '@/shared/grid-config';
import { UserPrefsService } from '@/shared/user-prefs.service';
import { AgFns } from '@/shared/ag-fns';

export interface WidgetDataDialogParams {
  title: string;
  data: unknown[];
  columnDefs: ColDef[];
  exportFileName?: string;
}

@Component({
  selector: 'app-widget-data-dialog',
  template: `
    <div class="flex flex-col h-full">
      <div class="flex-1 min-h-0">
        <ag-grid-angular
          class="w-full h-full"
          [ngClass]="userPrefsService.gridTheme"
          [rowData]="params.data"
          [columnDefs]="params.columnDefs"
          [defaultColDef]="defaultColDef"
          [rowHeight]="gridConfig.ROW_HEIGHT"
          [headerHeight]="gridConfig.HEADER_HEIGHT"
          [floatingFiltersHeight]="35"
          [pagination]="true"
          [paginationPageSize]="20"
          [paginationPageSizeSelector]="[10, 20, 50, 100]"
          [sideBar]="sideBar"
          [enableRangeSelection]="true"
          [rowGroupPanelShow]="'always'"
          [animateRows]="true"
          (gridReady)="onGridReady($event)"
        />
      </div>
      <div class="flex justify-between items-center p-3 border-t">
        <div class="flex gap-4 items-center">
          <a class="sizing-link" (click)="onAutoSizeColumns()">[ Autosize Columns ]</a>
          <a class="sizing-link" (click)="onSizeToFit()">[ Size to Fit ]</a>
        </div>
        <div class="flex gap-2 items-center">
          <button
            pButton
            icon="pi pi-download"
            label="Export to Excel"
            class="p-button-outlined p-button-sm"
            (click)="onExport()">
          </button>
          <button
            pButton
            label="Close"
            class="p-button-sm"
            (click)="onClose()">
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
    .sizing-link {
      cursor: pointer;
      color: var(--p-primary-color, #0077c5);
      text-decoration: none;
      font-size: 13px;
    }
    .sizing-link:hover {
      text-decoration: underline;
    }
  `],
  standalone: false,
})
export class WidgetDataDialogComponent implements OnInit {
  params!: WidgetDataDialogParams;
  gridApi!: GridApi;

  readonly gridConfig = GRID_CONFIG;

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    enableRowGroup: true,
    enablePivot: true,
    enableValue: true,
  };

  sideBar: SideBarDef = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: false,
          suppressValues: false,
          suppressPivots: false,
          suppressPivotMode: false,
        },
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
    defaultToolPanel: '',
  };

  constructor(
    private config: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    public userPrefsService: UserPrefsService,
  ) {}

  ngOnInit(): void {
    this.params = this.config.data as WidgetDataDialogParams;
    AgFns.updateColDefs(this.params.columnDefs);
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    AgFns.sizeColumnsToFit(this.gridApi);
  }

  onExport(): void {
    const fileName = this.params.exportFileName || 'widget-data';
    this.gridApi.exportDataAsExcel({
      fileName: `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`,
      sheetName: this.params.title,
    });
  }

  onClose(): void {
    this.ref.close();
  }

  onAutoSizeColumns(): void {
    this.gridApi.autoSizeAllColumns();
  }

  onSizeToFit(): void {
    this.gridApi.sizeColumnsToFit();
  }
}
